import { Button, Divider, Form, Spin, Timeline, Typography } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { match as routerMatch } from 'react-router-dom';
import { Dispatch } from 'redux';
import { administratorsListAction } from '~Administrators/actions';
import { administratorsSelector } from '~Administrators/selectors';
import IAdministrator from '~Api/Administrator/IAdministrator';
import IInvestor from '~Api/Investor/IInvestor';
import INote from '~Api/Investor/INote';
import {
    investorGetAction,
    investorNotesAddAction,
    investorNotesListAction,
} from '~Investors/actions';
import {
    investorNotesSelector,
    investorSelector,
} from '~Investors/selectors';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import Layout from './Layout';

interface IState {
    note: string;
}

interface IMatch {
    uuid: string;
}

interface IProps {
    match: routerMatch<IMatch>;
}

interface IPropsSelector {
    administrators: IDictionary<IAdministrator>;
    investor: IInvestor;
    notes: INote[];
}

interface IPropsDispatch {
    administratorsList: () => void;
    investorGet: () => void;
    noteAdd: (note: string) => void;
    notesList: () => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class Notes extends React.Component<Props, IState> {
    public state: IState = {
        note: null,
    };

    constructor(props: Props) {
        super(props);

        this.onChangeNote = this.onChangeNote.bind(this);
        this.onClickAddNote = this.onClickAddNote.bind(this);
    }

    public componentDidMount() {
        const { administrators, investor } = this.props;

        if (!investor) {
            this.props.investorGet();
        }

        if (!administrators) {
            this.props.administratorsList();
        }

        this.props.notesList();
    }

    public render(): JSX.Element {
        const { administrators, investor, match, notes } = this.props;
        const { note } = this.state;

        if (!administrators || !investor || !notes) {
            return (
                <Layout uuid={match.params.uuid} section='notes'>
                    <Spin/>
                </Layout>
            );
        }

        const unsavedNote: INote = _.find(notes, (loopNote: INote) => !loopNote.uuid);

        const unsavedNoteBlock: JSX.Element = unsavedNote && (
            <React.Fragment>
                <Typography.Text type='secondary'>{administrators[unsavedNote.administratorUuid].name}</Typography.Text><br/>
                <Typography.Text>{unsavedNote.note}</Typography.Text>
            </React.Fragment>
        );

        const notesBlock: JSX.Element[] = _.sortBy(_.filter(notes, (loopNote: INote) => !!loopNote.uuid), 'createdTime').map((loopNote: INote) => (
            <Timeline.Item key={loopNote.uuid} label={dayjs(loopNote.createdTime).format('YYYY-MM-DD - HH:mm')}>
                <Typography.Text type='secondary'>{administrators[loopNote.administratorUuid].name}</Typography.Text><br/>
                <Typography.Text>{loopNote.note}</Typography.Text>
            </Timeline.Item>
        ));

        return (
            <Layout uuid={match.params.uuid} section='notes'>
                <Typography.Title level={2}>Notes</Typography.Title>
                <Form.Item label='Note' className='note'>
                    <TextArea disabled={!!unsavedNote} onChange={this.onChangeNote} rows={15} value={note} />
                </Form.Item>
                <Button
                    className='add-note'
                    disabled={!note || !!unsavedNote}
                    onClick={this.onClickAddNote}
                    type='primary'
                >
                    Add Note
                </Button>
                {notesBlock.length > 0 && <Divider/>}
                <Timeline mode='left' pending={unsavedNoteBlock} reverse={true}>
                    {notesBlock}
                </Timeline>
            </Layout>
        );
    }

    private onChangeNote(event: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({
            note: event.target.value,
        });
    }

    private onClickAddNote() {
        const { note } = this.state;

        this.setState({
            note: null,
        });

        this.props.noteAdd(note);
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        administrators: administratorsSelector(state),
        investor: investorSelector(state, ownProps.match.params.uuid),
        notes: investorNotesSelector(state, ownProps.match.params.uuid),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        administratorsList: () => dispatch(administratorsListAction()),
        investorGet: () => dispatch(investorGetAction(ownProps.match.params.uuid)),
        noteAdd: (note: string) => dispatch(investorNotesAddAction(ownProps.match.params.uuid, note)),
        notesList: () => dispatch(investorNotesListAction(ownProps.match.params.uuid)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Notes);
